import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Eye,
    EyeOff,
    CheckCircle,
    XCircle
} from 'lucide-react';
import axiosInstance from '@/axios';
import Modal from '@/pages/Other/Modal';

const emptyForm = {
    id: '',
    page_slug: '',
    page_name: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    canonical_url: '',
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_title: '',
    twitter_description: '',
    twitter_image: '',
    schema_json: '', // stringified JSON in form
    is_active: true
};

const SeoPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('create'); // create | edit | view
    const [formData, setFormData] = useState(emptyForm);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(null); // file selected for upload
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadField, setUploadField] = useState(null);
    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/seo');
            setItems(res.data.data || res.data || []);
        } catch (err) {
            console.error('fetch seo error', err);
            window.alert('Failed to fetch SEO pages');
        } finally {
            setLoading(false);
        }
    };

    const filtered = items.filter(i =>
        (i.page_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.page_slug || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.meta_title || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // open create modal
    const openCreate = () => {
        setFormData(emptyForm);
        setModalType('create');
        setShowModal(true);
    };

    // open edit modal
    const openEdit = (item) => {
        // convert schema_json to pretty string if exists
        const schemaStr = item.schema_json ? JSON.stringify(item.schema_json, null, 2) : '';
        setFormData({ ...item, schema_json: schemaStr });
        setModalType('edit');
        setShowModal(true);
    };

    // open view modal
    const openView = (item) => {
        setSelectedItem(item);
        setModalType('view');
        setShowModal(true);
    };

    const handleInput = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateForm = () => {
        if (!formData.page_slug || !formData.page_name || !formData.meta_title) {
            window.alert('Please fill required fields: page_slug, page_name, meta_title');
            return false;
        }
        if (formData.schema_json) {
            try {
                JSON.parse(formData.schema_json);
            } catch (err) {
                window.alert('schema_json is not valid JSON');
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setLoading(true);
            // prepare payload: convert schema_json to JSON if present
            const payload = { ...formData };
            if (payload.schema_json) {
                payload.schema_json = JSON.parse(payload.schema_json);
            } else {
                payload.schema_json = null;
            }

            if (modalType === 'create') {
                const {id,...data}=payload
                await axiosInstance.post('/seo', data);
                window.alert('SEO page created');
            } else if (modalType === 'edit') {
                await axiosInstance.put(`/seo/${payload.id}`, payload);
                window.alert('SEO page updated');
            }

            setShowModal(false);
            await fetchItems();
        } catch (err) {
            console.error('submit seo error', err);
            window.alert('Failed to save SEO page');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure to delete this item?')) return;
        try {
            setLoading(true);
            await axiosInstance.delete(`/seo/${id}`);
            setItems(prev => prev.filter(i => i.id !== id));
            setSelectedIds(prev => prev.filter(x => x !== id));
            window.alert('Deleted successfully');
        } catch (err) {
            console.error('delete seo error', err);
            window.alert('Delete failed');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedIds.length} items?`)) return;

        try {
            setLoading(true);
            // if backend has /seo/bulk-delete, replace with single request
            // await axiosInstance.post('/seo/bulk-delete', { ids: selectedIds });

            // fallback: delete one-by-one
            await Promise.all(selectedIds.map(id => axiosInstance.delete(`/seo/${id}`)));
            setItems(prev => prev.filter(i => !selectedIds.includes(i.id)));
            setSelectedIds([]);
            window.alert('Bulk delete success');
        } catch (err) {
            console.error('bulk delete error', err);
            window.alert('Bulk delete failed');
        } finally {
            setLoading(false);
        }
    };

    const toggleActive = async (item) => {
        if (!window.confirm('Change active status?')) return;
        try {
            setLoading(true);
            const payload = { ...item, is_active: !item.is_active };
            await axiosInstance.put(`/seo/${item.id}`, payload);
            setItems(prev => prev.map(i => (i.id === item.id ? { ...i, is_active: !i.is_active } : i)));
        } catch (err) {
            console.error('toggle active error', err);
            window.alert('Failed to toggle status');
        } finally {
            setLoading(false);
        }
    };
    const handleFileSelect = (e, fieldName) => {
        const file = e.target.files[0];
        if (file) {
            setUploadingFile(file);
            setUploadField(fieldName);
        }
    };
    const handleUploadFile = async () => {
        if (!uploadingFile || !uploadField) return;

        setUploadLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadingFile);

            const res = await axiosInstance.post('/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (res.data && res.data.url) {
                // Set the returned URL into the correct form field
                setFormData(prev => ({
                    ...prev,
                    [uploadField]: res.data.url,
                }));

                // Clear upload state
                setUploadingFile(null);
                setUploadField(null);
            } else {
                alert('Upload failed: no URL returned');
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload file');
        } finally {
            setUploadLoading(false);
        }
    };
    return (
        <div className="container mx-auto px-5 mt-3">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold p-2">SEO Pages</h1>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name, slug or title..."
                            className="pl-9 pr-3 py-2 border rounded-lg"
                        />
                    </div>

                    {selectedIds.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            className="bg-red-500 text-white px-4 py-2 rounded-md"
                        >
                            Delete Selected ({selectedIds.length})
                        </button>
                    )}

                    <button
                        onClick={openCreate}
                        className="bg-[#264b82] text-white px-4 py-2 rounded-md flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" /> New SEO
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-auto">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
                    </div>
                ) : (
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="p-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.length > 0 && selectedIds.length === filtered.length}
                                        onChange={(e) => {
                                            if (e.target.checked) setSelectedIds(filtered.map(i => i.id));
                                            else setSelectedIds([]);
                                        }}
                                    />
                                </th>
                                <th className="text-left p-3">Page Name</th>
                                <th className="text-left p-3">Slug</th>
                                <th className="text-left p-3">Meta Title</th>
                                <th className="text-left p-3">Active</th>
                                <th className="text-left p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(item => (
                                <tr key={item.id} className="border-t hover:bg-gray-50">
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) setSelectedIds(prev => [...prev, item.id]);
                                                else setSelectedIds(prev => prev.filter(x => x !== item.id));
                                            }}
                                        />
                                    </td>
                                    <td className="p-3">{item.page_name}</td>
                                    <td className="p-3">{item.page_slug}</td>
                                    <td className="p-3">{item.meta_title}</td>
                                    <td className="p-3">
                                        {item.is_active ? (
                                            <span className="inline-flex items-center gap-1 text-green-600">
                                                <CheckCircle className="h-4 w-4" /> Active
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-red-500">
                                                <XCircle className="h-4 w-4" /> Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openView(item)}
                                                title="View"
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                <Eye className="h-4 w-4 text-gray-700" />
                                            </button>
                                            <button
                                                onClick={() => openEdit(item)}
                                                title="Edit"
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                <Edit2 className="h-4 w-4 text-indigo-600" />
                                            </button>
                                            <button
                                                onClick={() => toggleActive(item)}
                                                title="Toggle Active"
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                {item.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                title="Delete"
                                                className="p-2 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="h-4 w-4 text-red-600" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {filtered.length === 0 && !loading && (
                    <div className="p-6 text-center text-gray-500">No SEO pages found.</div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
                    <div className="bg-[#264b82] px-6 py-3 text-white rounded-t">
                        <h3 className="text-lg font-semibold">
                            {modalType === 'create' && 'Create SEO Page'}
                            {modalType === 'edit' && 'Edit SEO Page'}
                            {modalType === 'view' && 'View SEO Page'}
                        </h3>
                    </div>

                    <div className="p-6 max-h-[70vh] overflow-auto">
                        {modalType === 'view' && selectedItem && (
                            <div className="space-y-3">
                                <div><strong>Page name:</strong> {selectedItem.page_name}</div>
                                <div><strong>Slug:</strong> {selectedItem.page_slug}</div>
                                <div><strong>Meta title:</strong> {selectedItem.meta_title}</div>
                                <div><strong>Meta description:</strong> <div className="mt-1 p-2 bg-gray-50 rounded">{selectedItem.meta_description}</div></div>
                                <div><strong>Canonical:</strong> {selectedItem.canonical_url}</div>
                                <div><strong>OG title:</strong> {selectedItem.og_title}</div>
                                <div><strong>Schema JSON:</strong>
                                    <pre className="mt-2 p-3 bg-gray-50 rounded text-sm">{JSON.stringify(selectedItem.schema_json || {}, null, 2)}</pre>
                                </div>
                            </div>
                        )}

                        {(modalType === 'create' || modalType === 'edit') && (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <h3 className='bg-gray-500 p-3 text-white'>Page</h3>
                                <div>
                                    <label className="text-sm font-medium">Page Slug *</label>
                                    <input name="page_slug" value={formData.page_slug} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Page Name *</label>
                                    <input name="page_name" value={formData.page_name} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" />
                                </div>
                                <h3 className='bg-gray-500 p-3 text-white'> Meta</h3>
                                <div>
                                    <label className="text-sm font-medium">Meta Title *</label>
                                    <input name="meta_title" value={formData.meta_title} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" />
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Meta Description</label>
                                    <textarea name="meta_description" value={formData.meta_description} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" rows={3} />
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Meta Keywords (comma separated)</label>
                                    <input name="meta_keywords" value={formData.meta_keywords} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Canonical URL</label>
                                    <input name="canonical_url" value={formData.canonical_url} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" />
                                </div>
                                <h3 className='bg-gray-500 p-3 text-white'>OG</h3>
                                <div>
                                    <label className="text-sm font-medium">OG Title</label>
                                    <input name="og_title" value={formData.og_title} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" />
                                </div>

                                <div>
                                    <label className="text-sm font-medium">OG Description</label>
                                    <textarea name="og_description" value={formData.og_description} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" rows={2} />
                                </div>

                                <div>
                                    <label className="text-sm font-medium">OG Image URL</label>
                                    <input readOnly={true} name="og_image" value={formData.og_image} onChange={handleInput}
                                        className="w-full px-3 py-2 border  rounded" />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileSelect(e, 'og_image')}
                                        className="mb-2"
                                    />
                                    {uploadingFile && uploadField === 'og_image' && (
                                        <button
                                            type="button"
                                            onClick={handleUploadFile}
                                            disabled={uploadLoading}
                                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                        >
                                            {uploadLoading ? "Uploading..." : `Upload ${uploadingFile.name}`}
                                        </button>
                                    )}
                                </div>
                                <h3 className='bg-gray-500 p-3 text-white'>Twitter</h3>
                                <div>
                                    <label className="text-sm font-medium">Twitter Title</label>
                                    <input name="twitter_title" value={formData.twitter_title} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" />
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Twitter Description</label>
                                    <textarea name="twitter_description" value={formData.twitter_description} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded" rows={2} />
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Twitter Image URL</label>
                                    <input readOnly={true} name="twitter_image" value={formData.twitter_image} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded readonly" />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileSelect(e, 'twitter_image')}
                                        className="mb-2"
                                    />
                                    {uploadingFile && uploadField === 'twitter_image' && (
                                        <button
                                            type="button"
                                            onClick={handleUploadFile}
                                            disabled={uploadLoading}
                                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                        >
                                            {uploadLoading ? "Uploading..." : `Upload ${uploadingFile.name}`}
                                        </button>
                                    )}
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Active</label>
                                    <div className="mt-1">
                                        <label className="inline-flex items-center gap-2">
                                            <input type="checkbox" name="is_active" checked={!!formData.is_active} onChange={handleInput} />
                                            <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Schema JSON (optional)</label>
                                    <textarea name="schema_json" value={formData.schema_json} onChange={handleInput}
                                        className="w-full px-3 py-2 border rounded font-mono text-sm" rows={6}
                                        placeholder='{"@context":"https://schema.org","@type":"Course", ... }' />
                                </div>

                                <div className="flex justify-end gap-3 mt-4">
                                    <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded bg-gray-100">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={loading} className="px-4 py-2 rounded bg-[#264b82] text-white">
                                        {loading ? 'Saving...' : modalType === 'create' ? 'Create' : 'Update'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SeoPage;
